import { type ComponentType, useContext } from "react";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { TaskGroupBy } from "@common/types/task";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { WorkspaceAvatarGroup } from "@components/avatar/WorkspaceAvatarGroup";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import {
    CellText,
    type DataGridCellProps,
    useIsRowExpanded,
} from "@design-system/DataGrid";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { formatDateOnly } from "@helpers/date";
import {
    TaskClientIconColorsContext,
    TaskClientNamesContext,
} from "../../taskClientNamesContext";
import type { NestedTaskRow } from "../../types";
import {
    getGroupRowSummary,
    type TaskGroupSummary,
} from "../../TaskView/Cells/taskGroupSummary";
import type { TaskListCell } from "./taskRowCells";

interface SummaryProps {
    summary: TaskGroupSummary;
}

const EstimateSummary = ({ summary }: SummaryProps) =>
    summary.estimatedTime == null ? null : (
        <CellText
            text={formatDuration(summary.estimatedTime)}
            overflow="truncate"
        />
    );

const DueDateSummary = ({ summary }: SummaryProps) =>
    summary.dueDate ? (
        <CellText
            text={formatDateOnly(summary.dueDate, fullDateFullYearFormat)}
            overflow="truncate"
        />
    ) : null;

const AssigneesSummary = ({ summary }: SummaryProps) =>
    summary.assignees.length === 0 ? null : (
        <AvatarGroup
            items={summary.assignees}
            getKey={(assignee) => assignee.id}
            getLabel={(assignee) => assignee.name}
            renderAvatar={(assignee) => (
                <AvatarTooltip content={assignee.name}>
                    <UserAvatar user={assignee} />
                </AvatarTooltip>
            )}
            max={3}
        />
    );

const ClientsSummary = ({ summary }: SummaryProps) => {
    const clientNamesById = useContext(TaskClientNamesContext);
    const iconColorsById = useContext(TaskClientIconColorsContext);
    const workspaces = summary.workspaceIds.flatMap((id) => {
        const name = clientNamesById.get(id);
        return name
            ? [
                  {
                      workspace: {
                          id,
                          name,
                          iconColor: iconColorsById.get(id) ?? null,
                      },
                  },
              ]
            : [];
    });
    return workspaces.length === 0 ? null : (
        <WorkspaceAvatarGroup workspaces={workspaces} max={3} />
    );
};

/**
 * A column's `summary` on group rows: `Summary` over the group's tasks while
 * it is collapsed, blank while expanded and for the property the list is
 * grouped by.
 */
const toGroupSummary = ({
    Summary,
    hiddenWhenGroupedBy,
}: {
    Summary: ComponentType<SummaryProps>;
    hiddenWhenGroupedBy: TaskGroupBy | null;
}): TaskListCell => {
    const TaskGroupSummaryCell = ({
        context,
    }: DataGridCellProps<NestedTaskRow>) => {
        const isExpanded = useIsRowExpanded(context.row);
        const summary = isExpanded
            ? null
            : getGroupRowSummary(context.row.original, hiddenWhenGroupedBy);
        return summary ? <Summary summary={summary} /> : null;
    };
    return TaskGroupSummaryCell;
};

/** A collapsed group's total estimate. */
export const TaskEstimateSummary = toGroupSummary({
    Summary: EstimateSummary,
    hiddenWhenGroupedBy: null,
});

/** A collapsed group's latest open due date. */
export const TaskDueDateSummary = toGroupSummary({
    Summary: DueDateSummary,
    hiddenWhenGroupedBy: TaskGroupBy.DUE_DATE,
});

/** A collapsed group's assignees. */
export const TaskAssigneesSummary = toGroupSummary({
    Summary: AssigneesSummary,
    hiddenWhenGroupedBy: TaskGroupBy.ASSIGNEE,
});

/** A collapsed group's clients. */
export const TaskClientsSummary = toGroupSummary({
    Summary: ClientsSummary,
    hiddenWhenGroupedBy: TaskGroupBy.CLIENT,
});
